import { Table, Button, Space, Row, Col, Modal, Typography, Input, Card, message } from "antd";
import PageWrapper from "../../utils/PageWrapper";
import { EditOutlined, DeleteOutlined, FileWordFilled, PlusOutlined, CopyOutlined } from "@ant-design/icons";
import PageTitle from "../../utils/PageTitle";
import { ReceiptText } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import CotizacionesForm from "./forms/CotizacionesForm";
import moment from "moment";
import { useAuth } from "../../layout/auth";

export default function CotizacionesList() {
  const [cotizacionesList, setCotizacionesList] = useState([]);
  const [search, setSearch] = useState("");
  const [filteredCotizaciones, setFilteredCotizaciones] = useState([]);
  const [showCotizacionesModal, setShowCotizacionModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [selectedCotizacion, setSelectedCotizacion] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const auth = useAuth();

  const fetchCotizaciones = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/cotizaciones`);
      setCotizacionesList(res.data || []);
    } catch (err) {
      console.error("Error fetching cotizaciones:", err);
      message.error("Error al cargar las cotizaciones");
    } finally {
      setLoading(false);
    }
  };

  const handleCotizacionFormCancel = () => {
    setShowCotizacionModal(false);
    setSelectedCotizacion(null);
    setIsNew(false);
  };

  const handleCotizacionFormOpen = (record) => {
    setShowCotizacionModal(true);
    setSelectedCotizacion(record || null);
    setIsNew(!record); // If no record is passed, we are creating a new one
  };

  const wordStyle = {
    fontSize: "20px",
    color: "#27508F",
  };

  const handleDeleteCotizacion = async (record) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/cotizaciones/${record.CotizacionID}`);
      await fetchCotizaciones();
      message.success("Cotización eliminada correctamente");
      setSelectedRow(null);
      setSelectedCotizacion(null);
      setShowDeleteModal(false);
    } catch (err) {
      console.error("Error deleting cotización:", err);
      message.error("Error al eliminar la cotización");
    }
  };

  const handleCloneCotizacion = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/cotizaciones/clone/${selectedRow}`);
      await fetchCotizaciones();
      message.success("Cotización clonada correctamente");
      setSelectedRow(null);
      setSelectedCotizacion(null);
      setShowCloneModal(false);
    } catch (err) {
      console.error("Error cloning cotización:", err);
      message.error("Error al clonar la cotización");
    }
  };

  const handleDownloadDocument = async (record) => {
    try {
      const response = await axios({
        method: "get",
        url: `${process.env.REACT_APP_API_URL}/formatos/cotizacion/${record.CotizacionID}`,
        responseType: "blob",
      });

      const contentType = response.headers["content-type"];
      let fileName, mimeType;

      if (contentType === "application/zip") {
        fileName = `cotizacion_${record.CotizacionID}_${new Date().getTime()}.zip`;
        mimeType = "application/zip";
      } else if (contentType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
        fileName = `cotizacion_${record.CotizacionID}_${new Date().getTime()}.docx`;
        mimeType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      } else {
        fileName = `cotizacion_${record.CotizacionID}_${new Date().getTime()}.doc`;
        mimeType = "application/msword";
      }

      const blob = new Blob([response.data], { type: mimeType });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);

      message.success("Documento descargado correctamente");
    } catch (err) {
      console.error("Error downloading document:", err);
      message.error("Error al descargar el documento");
    }
  };

  const columns = [
    {
      title: "Solicitante",
      dataIndex: "Solicitante",
      key: "Solicitante",
      ellipsis: true,
    },
    {
      title: "Fecha de cotización",
      dataIndex: "FechaCotizacion",
      key: "FechaCotizacion",
      render: (text, record) => (
        <Row gutter={[0, 4]}>
          <Col span={24}>
            <span style={{ fontWeight: 500 }}>{moment(record.FechaCotizacion).format("DD/MM/YYYY")}</span>
          </Col>
          <Col span={24}>
            <span style={{ color: "#666", fontSize: "12px" }}>{moment(record.FechaCotizacion).fromNow()}</span>
          </Col>
        </Row>
      ),
    },
    {
      title: "Domicilio",
      render: (text, record) => (
        <Row gutter={[0, 4]}>
          <Col span={24}>
            <span style={{ fontWeight: 500 }}>
              {record.Calle} {record.NumeroExterior}
              {record.NumeroInterior ? `, ${record.NumeroInterior}` : ""}
            </span>
          </Col>
          <Col span={24}>
            <span style={{ color: "#666", fontSize: "12px" }}>
              {record.Colonia ? `Col. ${record.Colonia}, ` : ""}
              {record.Municipio}, {record.Estado}
            </span>
          </Col>
          <Col span={24}>
            <span style={{ color: "#999", fontSize: "11px" }}>CP: {record.CodigoPostal}</span>
          </Col>
        </Row>
      ),
    },
    {
      title: "Tipo Tensión",
      dataIndex: "TipoTension",
      key: "TipoTension",
      align: "center",
    },
    {
      title: "Carga Instalada",
      dataIndex: "CargaInstalada",
      key: "CargaInstalada",
      align: "right",
      render: (text) => `${text} kW`,
    },
    {
      title: "Precio Final",
      dataIndex: "PrecioFinal",
      key: "PrecioFinal",
      align: "right",
      render: (text, record) => (
        <span style={{ fontWeight: 600, color: "#1890ff" }}>
          {record.PrecioFinal?.toLocaleString("es-MX", {
            style: "currency",
            currency: "MXN",
          }) || "$0.00"}
        </span>
      ),
    },
    {
      title: "Acciones",
      dataIndex: "Acciones",
      key: "Acciones",
      align: "center",
      render: (text, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleCotizacionFormOpen(record)}
            title="Editar cotización"
            size="small"
          />
          <Button
            type="default"
            icon={<FileWordFilled style={wordStyle} />}
            onClick={() => handleDownloadDocument(record)}
            title="Descargar documento"
            size="small"
          />
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              setShowDeleteModal(true);
              setSelectedCotizacion(record);
            }}
            disabled={auth?.Rol !== "director"}
            title="Eliminar cotización"
            size="small"
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    fetchCotizaciones();
  }, []);

  useEffect(() => {
    if (search && cotizacionesList) {
      const filtered = cotizacionesList.filter((cotizacion) => {
        const searchLower = search.toLowerCase();
        return (
          cotizacion?.Solicitante?.toString().toLowerCase().includes(searchLower) ||
          cotizacion?.Calle?.toString().toLowerCase().includes(searchLower) ||
          cotizacion?.Municipio?.toString().toLowerCase().includes(searchLower) ||
          cotizacion?.Estado?.toString().toLowerCase().includes(searchLower) ||
          cotizacion?.Colonia?.toString().toLowerCase().includes(searchLower) ||
          cotizacion?.CodigoPostal?.toString().includes(search)
        );
      });
      setFilteredCotizaciones(filtered);
    } else {
      setFilteredCotizaciones(cotizacionesList);
    }
  }, [search, cotizacionesList]);

  const handleRowSelect = (record, selected) => {
    if (selected) {
      setSelectedRow(record.CotizacionID);
      setSelectedCotizacion(record);
    } else {
      setSelectedRow(null);
      setSelectedCotizacion(null);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  const clearSearch = () => {
    setSearch("");
  };

  return (
    <PageWrapper className="cotizaciones-main-card">
      <PageTitle icon={<ReceiptText />} title="Cotizaciones" />

      {/* Controls Section */}
      <div
        style={{
          padding: "16px",
          background: "rgba(249, 250, 251, 0.8)",
          borderRadius: "12px",
          border: "1px solid rgba(229, 231, 235, 0.6)",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05)",
          marginBottom: "24px",
        }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Input.Search
              placeholder="Buscar por solicitante, calle, municipio, estado, colonia o código postal..."
              onSearch={handleSearch}
              onChange={(e) => setSearch(e.target.value)}
              value={search}
              enterButton="Buscar"
              size="large"
              allowClear
              onClear={clearSearch}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ textAlign: "right" }}>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="large"
                onClick={() => handleCotizacionFormOpen()}
                style={{
                  borderRadius: "8px",
                  height: "40px",
                }}
              >
                Agregar
              </Button>
              <Button
                type="default"
                icon={<CopyOutlined />}
                size="large"
                onClick={() => setShowCloneModal(true)}
                disabled={!selectedRow}
                style={{
                  borderRadius: "8px",
                  height: "40px",
                  background: selectedRow ? "#722ed1" : undefined,
                  borderColor: selectedRow ? "#722ed1" : undefined,
                  color: selectedRow ? "#fff" : undefined,
                }}
              >
                Clonar
              </Button>
              <Button
                type="danger"
                icon={<DeleteOutlined />}
                size="large"
                onClick={() => {
                  if (selectedCotizacion) {
                    setShowDeleteModal(true);
                  }
                }}
                disabled={auth?.Rol !== "director" || !selectedCotizacion}
                style={{
                  borderRadius: "8px",
                  height: "40px",
                }}
              >
                Eliminar
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <Table
        rowKey={(record) => record.CotizacionID}
        columns={columns}
        dataSource={filteredCotizaciones}
        loading={loading}
        rowSelection={{
          type: "radio",
          onSelect: handleRowSelect,
          selectedRowKeys: selectedRow ? [selectedRow] : [],
        }}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} cotizaciones`,
          pageSizeOptions: ["10", "20", "50", "100"],
          defaultPageSize: 10,
        }}
        scroll={{ x: true }}
      />

      <CotizacionesForm
        visible={showCotizacionesModal}
        onCancel={handleCotizacionFormCancel}
        setCotizacionesList={setCotizacionesList}
        record={selectedCotizacion}
        isNew={isNew}
      />

      <Modal
        title="Eliminar cotización"
        visible={showDeleteModal}
        onOk={() => handleDeleteCotizacion(selectedCotizacion)}
        onCancel={() => setShowDeleteModal(false)}
        okText="Eliminar"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
      >
        <p>
          ¿Está seguro de eliminar la cotización de <strong>{selectedCotizacion?.Solicitante}</strong>?
        </p>
        <p style={{ color: "#ff4d4f", fontSize: "12px" }}>Esta acción no se puede deshacer.</p>
      </Modal>

      <Modal
        title="Clonar cotización"
        visible={showCloneModal}
        onOk={handleCloneCotizacion}
        onCancel={() => setShowCloneModal(false)}
        okText="Clonar"
        cancelText="Cancelar"
      >
        <p>
          ¿Está seguro de clonar la cotización de <strong>{selectedCotizacion?.Solicitante}</strong>?
        </p>
        <p style={{ color: "#666", fontSize: "12px" }}>Se creará una nueva cotización con los mismos datos.</p>
      </Modal>
    </PageWrapper>
  );
}
